import './CasePage.scss';

import React, { useMemo, useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { NavLink, generatePath } from 'react-router-dom';

import { mutationFileForm, mutationMainForm } from 'network';
import { dictionariesSelector } from 'store';
import { PATH } from 'router';
import { useIntl } from 'react-intl';
import { cn as classNames } from 'ui/utils/bem';

import { Breadcrumbs } from '@dataartdev/uikit/Breadcrumbs';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { Typography } from '@dataartdev/uikit/Typography';
import { MemberCard } from '@dataartdev/uikit/MemberCard';
import { ListLinks } from '@dataartdev/uikit/ListLinks';
import { Article } from '../../../common/components/Article/Article';
import { TreeNav } from '@dataartdev/uikit/TreeNav';
import { FileCard } from '../../../common/components';
import { TagGroup } from '@dataartdev/uikit/TagGroup';
import { Picture } from '@dataartdev/uikit/Picture';
import { SocialsShare } from '@dataartdev/uikit/SocialsShare';
import { Mq } from '@dataartdev/uikit/Mq';
import { NoteText } from '../../../common/components/NoteText/NoteText';
import { recaptchaKey, recaptchaFormKey } from 'common/constants';

import { ContentBuilderItem } from '../../../common/components/ContentBuilder/ContentBuilderItem';
import { CtaScrollerBtn } from '../../../common/components/CtaScrollerBtn';
import { CSRightSideGeneration } from './CSRightSideGeneration';
import { SiteBlock } from '../../../common/components/SiteBlock/SiteBlock';
import { onSocialShareHandler } from '../../../common/utils/socialShareHandler';
import { ICasePage } from './models/CasePage.props';
import { BodyType } from '../../../common/components/ContentBuilder/ContentBuilder';
import { useCameFrom } from '../../../common/hooks/useCameFrom';
import { useLetsTalkFormDictionary } from '../../../common/hooks/useLetsTalkFormDictionary';

const cnCasePage = classNames('CasePage');

type Props = RouteComponentProps & {
  data: ICasePage | null;
};

export const RegularCSPage: React.FC<Props> = ({ history, data }) => {
  const { companySiteCaseStudy } = useSelector(dictionariesSelector) || {};
  const {
    companySiteCaseStudyServices = '',
    companySiteCaseStudyTechnologies = '',
    companySiteCaseStudyMembersTitle = '',
    companySiteCaseStudyIndustry = '',
    companySiteCaseStudyCompanyName = '',
    companySiteCaseStudyLocation = '',
    companySiteCaseStudyShare = '',
    companySiteCaseStudyMoreCases = '',
    // companySiteCaseStudyCardText = '',
    // companySiteCaseStudySideLandingLink = '',
    // companySiteCaseStudy18caseStudiesSectionCategory = '',
    // companySiteCaseStudySidePostLink = '',
  } = companySiteCaseStudy || {};
  const {
    companySiteForm,
    dictionariesForm,
    companySiteTableOfContent,
    companySiteFormLetsTalkText,
    companySiteFormLetsTalkTitle,
  } = useLetsTalkFormDictionary();

  const {
    title,
    clientTitle,
    clientLogo,
    clientSlug,
    tags = [],
    techStock = [],
    industry,
    locationText,
    services = [],
    achievements,
    clientDescription,
    challenge,
    solution,
    text,
    sideChat = [],
    members = [],
    servicesBlock,
    conclusion,
    files = [],
    relatedCaseStudies = [],
    contentCarousel,
    contentCarouselText,
    contentCarouselShotCode,
    firstButton,
    secondButton,
    linkedWebcites,
    companyBlogPost,
    landing,
    form,
    breadcrumbs = [],
    id,
  } = data || {};

  const pageRef = useRef(null);
  const { cameFrom } = useCameFrom();
  const submitButtonID =
    companySiteForm?.companySiteFormDownloadFile
      ?.companySiteFormDownloadFileTrackingID || 'file_download';

  const { locale } = useIntl();
  const lang = locale === 'en' ? undefined : locale;

  const tagsRender = useMemo(
    () => (
      <React.Fragment>
        {techStock && (
          <div className={cnCasePage('InfoBlock-right')}>
            <TagGroup
              gap="xs"
              items={techStock.map(tag => {
                return {
                  ...tag,
                  onClick: () =>
                    history.push(
                      generatePath(`${PATH.SEARCH_PAGE}?tags=${tag.id}`, {
                        lang,
                      })
                    ),
                };
              })}
            />
          </div>
        )}
      </React.Fragment>
    ),
    [techStock, history, lang]
  );

  return (
    <React.Fragment>
      <div className={cnCasePage()}>
        <Mq query="--desktop">
          <TreeNav title={companySiteTableOfContent} pageRef={pageRef} fixed />
        </Mq>
        <div className="container">
          <div className={cnCasePage('Breadcrumbs')}>
            {breadcrumbs.length > 0 && (
              <Breadcrumbs
                getLabel={page => page.text}
                getLink={page => page.url}
                history={history}
                pages={breadcrumbs}
              />
            )}
          </div>
          <div className={cnCasePage('Body')}>
            <Grid
              breakpoints={{
                desktop: { cols: 12, gap: 'xl' },
                tablet: { cols: 8 },
              }}
            >
              <GridItem
                breakpoints={{ desktop: { col: 8 }, tablet: { col: 8 } }}
              >
                {title && (
                  <Typography.Title
                    size="lg"
                    className={cnCasePage('Title')}
                    as="h1"
                  >
                    {title}
                  </Typography.Title>
                )}
                {firstButton && (
                  <SiteBlock topGap="3xl" bottomGap="5xl" useMargin>
                    <CtaScrollerBtn {...firstButton} />
                  </SiteBlock>
                )}
                <Mq query="--tablet-max">
                  <Grid breakpoints={{ tablet: { cols: 8, gap: 'm' } }}>
                    <GridItem breakpoints={{ tablet: { col: 5 } }}>
                      <div className={cnCasePage('InfoBlock')}>
                        {clientTitle && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyCompanyName && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                  as="h2"
                                >
                                  {companySiteCaseStudyCompanyName}
                                </Typography.Text>
                              )}
                            </div>
                            {clientTitle && clientSlug && (
                              <div className={cnCasePage('InfoBlock-right')}>
                                <NavLink
                                  to={generatePath(PATH.CLIENT_PAGE, {
                                    slug: clientSlug,
                                    lang,
                                  })}
                                >
                                  <Typography.Text weight="bold" size="m">
                                    {clientTitle}
                                  </Typography.Text>
                                </NavLink>
                              </div>
                            )}
                          </div>
                        )}
                        {locationText && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyLocation && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                  as="h2"
                                >
                                  {companySiteCaseStudyLocation}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnCasePage('InfoBlock-right')}>
                              <Typography.Text weight="bold" size="m">
                                {locationText}
                              </Typography.Text>
                            </div>
                          </div>
                        )}
                        {industry && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyIndustry && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                  as="h2"
                                >
                                  {companySiteCaseStudyIndustry}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnCasePage('InfoBlock-right')}>
                              {industry.title && industry.slug && (
                                <NavLink
                                  to={generatePath(PATH.INDUSTRIES_PAGE, {
                                    slug: industry.slug,
                                  })}
                                >
                                  <Typography.Text weight="bold" size="m">
                                    {industry.title}
                                  </Typography.Text>
                                </NavLink>
                              )}
                            </div>
                          </div>
                        )}
                        {services?.length > 0 && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyServices && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                  as="h2"
                                >
                                  {companySiteCaseStudyServices}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnCasePage('InfoBlock-right')}>
                              {services.map(
                                page =>
                                  page.slug &&
                                  page.title && (
                                    <NavLink
                                      key={page.slug}
                                      to={generatePath(PATH.SERVICES_PAGE, {
                                        slug: page.slug,
                                      })}
                                    >
                                      <Typography.Text weight="bold" size="m">
                                        {page.title}
                                      </Typography.Text>
                                    </NavLink>
                                  )
                              )}
                            </div>
                          </div>
                        )}
                        {techStock?.length > 0 && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyTechnologies && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                  as="h2"
                                  nowrap
                                >
                                  {companySiteCaseStudyTechnologies}
                                </Typography.Text>
                              )}
                            </div>
                            {tagsRender}
                          </div>
                        )}
                      </div>
                    </GridItem>
                    <GridItem breakpoints={{ tablet: { col: 3 } }}>
                      {clientLogo &&
                        clientSlug &&
                        Object.keys(clientLogo).length > 0 && (
                          <NavLink
                            to={generatePath(PATH.CLIENT_PAGE, {
                              slug: clientSlug,
                              lang,
                            })}
                          >
                            <div className={cnCasePage('Logo')}>
                              <Picture {...clientLogo} />
                            </div>
                          </NavLink>
                        )}
                    </GridItem>
                  </Grid>
                </Mq>

                {achievements && (
                  <div
                    className={cnCasePage('Advantages')}
                    style={{ backgroundColor: '#' + achievements.color }}
                  >
                    {achievements.columnI && (
                      <div className={cnCasePage('Advantages-Column')}>
                        {achievements.columnI.title && (
                          <Typography.Title
                            size="lg"
                            className={cnCasePage('Advantages-Title')}
                            as="h2"
                          >
                            {achievements.columnI.title}
                          </Typography.Title>
                        )}
                        {achievements.columnI.subTitle && (
                          <Typography.Title
                            size="tiny"
                            className={cnCasePage('Advantages-SubTitle')}
                            as="h2"
                          >
                            {achievements.columnI.subTitle}
                          </Typography.Title>
                        )}
                        {achievements.columnI.text && (
                          <Typography.Title
                            size="tiny"
                            className={cnCasePage('Advantages-Text')}
                            as="h2"
                          >
                            {achievements.columnI.text}
                          </Typography.Title>
                        )}
                      </div>
                    )}
                    {achievements.columnII && (
                      <div className={cnCasePage('Advantages-Column')}>
                        {achievements.columnII.title && (
                          <Typography.Title
                            size="lg"
                            className={cnCasePage('Advantages-Title')}
                            as="h2"
                          >
                            {achievements.columnII.title}
                          </Typography.Title>
                        )}
                        {achievements.columnII.subTitle && (
                          <Typography.Title
                            size="tiny"
                            className={cnCasePage('Advantages-SubTitle')}
                            as="h2"
                          >
                            {achievements.columnII.subTitle}
                          </Typography.Title>
                        )}
                        {achievements.columnII.text && (
                          <Typography.Title
                            size="tiny"
                            className={cnCasePage('Advantages-Text')}
                            as="h2"
                          >
                            {achievements.columnII.text}
                          </Typography.Title>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {clientDescription && (
                  <NoteText
                    strings={clientDescription.strings}
                    title={clientDescription.title}
                    titleTag="h3"
                    content={clientDescription.text}
                  />
                )}
                {challenge && (
                  <NoteText
                    strings={challenge.strings}
                    title={challenge.title}
                    titleTag="h3"
                    content={challenge.text}
                  />
                )}
                {solution && (
                  <NoteText
                    strings={solution.strings}
                    title={solution.title}
                    titleTag="h3"
                    content={solution.text}
                  />
                )}

                <div className={cnCasePage('Content')} ref={pageRef}>
                  <Article
                    content={text}
                    slider={{
                      code: contentCarouselShotCode,
                      items: contentCarousel,
                      text: contentCarouselText,
                    }}
                    chatTooltips={sideChat?.length > 0 && sideChat}
                    scrollerBtn={{
                      code: '{{scrollerBtn}}',
                      item: secondButton,
                    }}
                  />
                </div>

                {members?.length > 0 && (
                  <div className={cnCasePage('Members')}>
                    {companySiteCaseStudyMembersTitle && (
                      <Typography.Text
                        size="xs"
                        weight="bold"
                        transform="uppercase"
                        className={cnCasePage('SectionTitle')}
                        as="h2"
                      >
                        {companySiteCaseStudyMembersTitle}
                      </Typography.Text>
                    )}

                    <Grid
                      breakpoints={{
                        desktop: { cols: 12, rowGap: 'xl', colGap: '2xl' },
                        tablet: { cols: 12, rowGap: 'm', colGap: 'xs' },
                        mobile: { cols: 1, gap: 'xl' },
                      }}
                    >
                      {members.map(member => (
                        <GridItem
                          key={member.id}
                          breakpoints={{
                            desktop: { col: 4 },
                            tablet: { col: 4 },
                          }}
                        >
                          <MemberCard
                            {...member}
                            routePath={PATH.TEAM_MEMBER}
                          />
                        </GridItem>
                      ))}
                    </Grid>
                  </div>
                )}
                {servicesBlock && (
                  <div className={cnCasePage('ServicesBlock')}>
                    <Typography.Text
                      size="xs"
                      weight="bold"
                      transform="uppercase"
                      className={cnCasePage('SectionTitle')}
                      as="h2"
                    >
                      {servicesBlock.title}
                    </Typography.Text>
                    <Typography.Text className={cnCasePage('SectionSubTitle')}>
                      {servicesBlock.subtitle}
                    </Typography.Text>
                    <Grid
                      breakpoints={{
                        desktop: { cols: 12, gap: 'xl' },
                        tablet: { cols: 8, gap: 'm' },
                      }}
                    >
                      <GridItem
                        breakpoints={{
                          desktop: { col: 6 },
                          tablet: { col: 4 },
                        }}
                      >
                        <ListLinks
                          items={servicesBlock.columnI}
                          getLabel={item => item.title}
                          getPath={item => item.link}
                        />
                      </GridItem>
                      <GridItem
                        breakpoints={{
                          desktop: { col: 6 },
                          tablet: { col: 4 },
                        }}
                      >
                        <ListLinks
                          items={servicesBlock.columnII}
                          getLabel={item => item.title}
                          getPath={item => item.link}
                        />
                      </GridItem>
                    </Grid>
                    <Article content={servicesBlock.text} />
                  </div>
                )}
                {conclusion && (
                  <NoteText
                    title={conclusion.title}
                    strings={conclusion.strings}
                    titleTag="h3"
                    content={conclusion.text}
                  />
                )}
                {files?.length > 0 && (
                  <Grid
                    breakpoints={{
                      desktop: { gap: 'xs', cols: 1 },
                      tablet: { gap: 'xs', cols: 1 },
                      mobile: { gap: 'xs', cols: 1 },
                    }}
                    className={cnCasePage('Files')}
                  >
                    {files.map(file => (
                      <GridItem key={file.file?.url} col="1">
                        <FileCard
                          url={file.file?.url}
                          title={file.title}
                          extension={file.file?.type}
                          size={file.file?.size}
                          date={file.file?.updatedAt}
                          onSubmit={async (body: BodyType) =>
                            await mutationFileForm({
                              body: {
                                ...body,
                                filePath: file.file?.url,
                                pageId: id,
                                [`${recaptchaFormKey}`]: body.token,
                                cameFrom: cameFrom,
                                trackingId: submitButtonID,
                              },
                            })
                          }
                          recaptchaKey={recaptchaKey}
                        />
                      </GridItem>
                    ))}
                  </Grid>
                )}
              </GridItem>
              <GridItem
                breakpoints={{
                  desktop: { col: 4, colStart: 9 },
                  tablet: { col: 8 },
                }}
              >
                <div className={cnCasePage('RightBox')}>
                  <Mq query="--desktop">
                    {(clientTitle ||
                      locationText ||
                      industry ||
                      services?.length > 0 ||
                      techStock?.length > 0) && (
                      <div className={cnCasePage('InfoBlock')}>
                        {clientTitle && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyCompanyName && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyCompanyName}
                                </Typography.Text>
                              )}
                            </div>
                            {clientTitle && clientSlug && (
                              <div className={cnCasePage('InfoBlock-right')}>
                                <NavLink
                                  to={generatePath(PATH.CLIENT_PAGE, {
                                    slug: clientSlug,
                                    lang,
                                  })}
                                >
                                  <Typography.Text weight="bold" size="m">
                                    {clientTitle}
                                  </Typography.Text>
                                </NavLink>
                              </div>
                            )}
                          </div>
                        )}
                        {locationText && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyLocation && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyLocation}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnCasePage('InfoBlock-right')}>
                              <Typography.Text weight="bold" size="m">
                                {locationText}
                              </Typography.Text>
                            </div>
                          </div>
                        )}
                        {industry && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyIndustry && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyIndustry}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnCasePage('InfoBlock-right')}>
                              {industry.slug && industry.title && (
                                <NavLink
                                  to={generatePath(PATH.INDUSTRIES_PAGE, {
                                    slug: industry.slug,
                                    lang,
                                  })}
                                >
                                  <Typography.Text weight="bold" size="m">
                                    {industry.title}
                                  </Typography.Text>
                                </NavLink>
                              )}
                            </div>
                          </div>
                        )}
                        {services?.length > 0 && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyServices && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyServices}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnCasePage('InfoBlock-right')}>
                              {services.map(
                                page =>
                                  page.slug &&
                                  page.title && (
                                    <NavLink
                                      key={page.slug}
                                      to={generatePath(PATH.SERVICES_PAGE, {
                                        slug: page.slug,
                                        lang,
                                      })}
                                    >
                                      <Typography.Text weight="bold" size="m">
                                        {page.title}
                                      </Typography.Text>
                                    </NavLink>
                                  )
                              )}
                            </div>
                          </div>
                        )}
                        {techStock?.length > 0 && (
                          <div className={cnCasePage('InfoBlock-Item')}>
                            <div className={cnCasePage('InfoBlock-Left')}>
                              {companySiteCaseStudyTechnologies && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                  nowrap
                                >
                                  {companySiteCaseStudyTechnologies}
                                </Typography.Text>
                              )}
                            </div>
                            {tagsRender}
                          </div>
                        )}
                        {clientLogo &&
                          clientSlug &&
                          Object.keys(clientLogo).length > 0 && (
                            <NavLink
                              to={generatePath(PATH.CLIENT_PAGE, {
                                slug: clientSlug,
                                lang,
                              })}
                            >
                              <div className={cnCasePage('Logo')}>
                                <Picture {...clientLogo} />
                              </div>
                            </NavLink>
                          )}
                      </div>
                    )}
                  </Mq>

                  <CSRightSideGeneration
                    landing={landing}
                    blog={companyBlogPost}
                    webSitesLinks={linkedWebcites}
                  />

                  {relatedCaseStudies?.length > 0 && (
                    <div className={cnCasePage('Recommendation')}>
                      <div className={cnCasePage('RecommendationTitle')}>
                        {companySiteCaseStudyMoreCases && (
                          <Typography.Text
                            size="xs"
                            weight="bold"
                            transform="uppercase"
                            as="span"
                          >
                            {companySiteCaseStudyMoreCases}
                          </Typography.Text>
                        )}
                      </div>
                      <div className={cnCasePage('RecommendationList')}>
                        {relatedCaseStudies.map(
                          item =>
                            item.slug &&
                            item.title && (
                              <Typography.Title
                                size="tiny"
                                key={item.id}
                                as="link"
                                to={generatePath(PATH.CASE_PAGE, {
                                  slug: item.slug,
                                  lang,
                                })}
                              >
                                {item.title}
                              </Typography.Title>
                            )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </GridItem>
            </Grid>
          </div>
          <div className={cnCasePage('Bottom')}>
            <Grid
              breakpoints={{ desktop: { cols: 2 }, tablet: { cols: 2 } }}
              yAlign="center"
            >
              <GridItem className={cnCasePage('BottomLeft')}>
                {tags.length > 0 && (
                  <TagGroup
                    items={tags.map(tag => {
                      return {
                        ...tag,
                        onClick: () =>
                          history.push(
                            generatePath(`${PATH.SEARCH_PAGE}?tags=${tag.id}`, {
                              lang,
                            })
                          ),
                      };
                    })}
                  />
                )}
              </GridItem>
              <GridItem className={cnCasePage('BottomRight')}>
                <SocialsShare
                  title={companySiteCaseStudyShare}
                  url={window.location.href}
                  onClick={onSocialShareHandler}
                />
              </GridItem>
            </Grid>
          </div>
        </div>
        <ContentBuilderItem
          type={3}
          id="test"
          aliases={PATH}
          topGap="xxl"
          pageId={id}
          title={companySiteFormLetsTalkTitle ?? form?.title ?? 'Lorem title'}
          text={companySiteFormLetsTalkText ?? form?.text ?? 'Lorem text'}
          member={form?.member}
          onSubmitForm={async body => await mutationMainForm({ body })}
          dictionariesForm={dictionariesForm}
        />
      </div>
    </React.Fragment>
  );
};
